import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbDatepickerI18n, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { BaseListComponent } from "src/app/shared/components/base-list/base-list.component";
import { BasePagingResponse } from "src/app/core/models/responses/base-paging-response";
import { Currency } from "src/app/models/enums/currency";
import { CurrencySubTypes } from "src/app/models/enums/currency-sub-types";
import { ExchangeItem } from "src/app/models/exchange/exchange-item-request";
import {
  ExchangeCostDetail,
  ExchangeCostDetailListRequest,
  ExchangeCostDirection,
} from "src/app/models/exchange/exchange-cost-detail";
import { ExchangeService } from "src/app/services/exchange.service";
import {
  DatePickerTrLangService,
  I18n,
} from "src/app/services/date-picker-tr-lang.service";
import { CustomCurrencyPipe } from "src/app/shared/pipes/custom-currency.pipe";
import { SubTypeDisplayPipe } from "src/app/shared/pipes/sub-type-display.pipe";

/**
 * Alım/Satım Maliyetleri ekranındaki bir birimin detayı: üstte özet, altında o birimin girdiği ve
 * çıktığı işlemler sayfalı olarak (alim-satim-maliyet.md).
 *
 * Birim ve tarih aralığı sorgu parametreleriyle gelir, böylece maliyet ekranındaki filtre
 * korunur ve sayfa bağlantısı paylaşılabilir.
 */
@Component({
  selector: "app-unit-cost-detail",
  templateUrl: "./unit-cost-detail.component.html",
  styleUrls: ["./unit-cost-detail.component.scss"],
  providers: [
    I18n,
    { provide: NgbDatepickerI18n, useClass: DatePickerTrLangService },
    CustomCurrencyPipe,
    SubTypeDisplayPipe,
  ],
})
export class UnitCostDetailComponent extends BaseListComponent implements OnInit {
  Currency = Currency;
  CurrencySubTypes = CurrencySubTypes;
  Direction = ExchangeCostDirection;

  currency: Currency;
  subType: CurrencySubTypes = null;
  title = "Maliyet Detayı";
  detail: ExchangeCostDetail;
  detailLoading = false;
  listResponse: BasePagingResponse<ExchangeItem>;
  beginDateModel: NgbDateStruct;
  endDateModel: NgbDateStruct;

  listRequest: ExchangeCostDetailListRequest = {
    currency: null,
    subType: null,
    beginDate: null,
    endDate: null,
    direction: ExchangeCostDirection.All,
    pageIndex: this.pageIndex,
    itemPerPage: this.itemPerPage,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ExchangeService,
    private customCurrencyPipe: CustomCurrencyPipe,
    private subTypePipe: SubTypeDisplayPipe,
  ) {
    super();
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.currency = Number(params["currency"]);
      this.subType =
        params["subType"] === undefined || params["subType"] === null || params["subType"] === ""
          ? null
          : Number(params["subType"]);
      this.beginDateModel = this.toDateStruct(params["beginDate"]);
      this.endDateModel = this.toDateStruct(params["endDate"]);

      this.title = this.buildTitle();
      this.listRequest.pageIndex = 1;
      this.reload();
    });
  }

  /** Özet ve liste aynı filtreyi kullanır. */
  reload() {
    this.listRequest.currency = this.currency;
    this.listRequest.subType = this.subType;
    this.listRequest.beginDate = this.toDate(this.beginDateModel, 0, 0, 0);
    this.listRequest.endDate = this.toDate(this.endDateModel, 23, 59, 59);

    this.reloadDetail();
    this.reloadList();
  }

  reloadDetail() {
    this.detailLoading = true;
    this.service
      .getCostDetail({
        currency: this.listRequest.currency,
        subType: this.listRequest.subType,
        beginDate: this.listRequest.beginDate,
        endDate: this.listRequest.endDate,
      })
      .toPromise()
      .then((res) => {
        this.detailLoading = false;
        this.detail = res?.data;
      });
  }

  reloadList() {
    this.Loading = true;
    this.service
      .getCostDetailList(this.listRequest)
      .toPromise()
      .then((res) => {
        this.Loading = false;
        this.listResponse = res;
      });
  }

  /** Yön değişince listeye baştan başlanır; özet yönden etkilenmez. */
  changeDirection(direction: ExchangeCostDirection) {
    if (this.listRequest.direction === direction) return;
    this.listRequest.direction = direction;
    this.listRequest.pageIndex = 1;
    this.reloadList();
  }

  dateChanged() {
    this.listRequest.pageIndex = 1;
    this.reload();
  }

  clearDates() {
    this.beginDateModel = null;
    this.endDateModel = null;
    this.dateChanged();
  }

  backToCosts() {
    this.router.navigateByUrl("/case/unit-costs");
  }

  /** İşlemde bu birim dükkâna mı girmiş? */
  /**
   * Defter stoğu ile kasa bakiyesi arasında anlamlı bir fark var mı. Maliyet defteri yalnızca
   * alım/satım işlemlerinden beslenir; tahsilat, perakende tahsilatı, şube transferi ve para
   * giriş/çıkışı kasaya yansır ama deftere yansımaz.
   */
  hasStockGap(): boolean {
    return !!this.detail && Math.abs(this.detail.stockDifference) > 0.0005;
  }

  isPurchase(item: ExchangeItem): boolean {
    return (
      Number(item.paymentCurrency) === Number(this.currency) &&
      this.sameSubType(item.paymentSubType)
    );
  }

  /** İşlemde geçen bu birimin miktarı. */
  amountOf(item: ExchangeItem): number {
    return this.isPurchase(item) ? item.paymentAmount : item.exchangeAmount;
  }

  private sameSubType(subType: CurrencySubTypes): boolean {
    const left = subType === undefined || subType === null ? null : Number(subType);
    const right = this.subType === undefined || this.subType === null ? null : Number(this.subType);
    return left === right;
  }

  private buildTitle(): string {
    const name = this.customCurrencyPipe.transform(this.currency);
    const sub = this.subType === null ? "" : " " + this.subTypePipe.transform(this.subType);
    return `${name}${sub} — Maliyet Detayı`;
  }

  /** Sorgu parametresi yyyy-MM-dd gelir; saat dilimi kaydırmasın diye elle ayrıştırılır. */
  private toDateStruct(value: string): NgbDateStruct {
    if (!value) return null;
    const parts = String(value).split("-");
    if (parts.length >= 3) {
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2].substring(0, 2));
      if (!isNaN(year) && !isNaN(month) && !isNaN(day))
        return { year: year, month: month, day: day };
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }

  private toDate(model: NgbDateStruct, hours: number, minutes: number, seconds: number): Date {
    if (!model) return null;
    const date = new Date();
    date.setFullYear(model.year, model.month - 1, model.day);
    date.setHours(hours, minutes, seconds, 0);
    return date;
  }
}
